import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext.js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Paginator } from 'primereact/paginator';
import { Calendar } from 'primereact/calendar';
import { ScrollTop } from 'primereact/scrolltop';
import { ProgressBar } from 'primereact/progressbar';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import Login from './Login.js';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import supportClientLogsApi from '../api/supportClientLogsApi.js';
        

const SupportClientLogs = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { type, id } = useParams();
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const { logout } = useAuth();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    log: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS},
    'client.name': { value: null, matchMode: FilterMatchMode.CONTAINS},
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [membership, setMembership] = useState('');
  const [appliedMembership, setAppliedMembership] = useState('');
  const [dataloaded, setDataLoaded] = useState('');
  const [showdebugmessage, setShowDebugMessage] = useState('');
  const [appid, setAppId] = useState(0);
  const [useAppId, setUseAppId] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const toast = React.useRef(null);

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        if (toast.current) {
            toast.current.show({severity: 'success', summary: '', detail: 'Login successful! You can continue working.'});
        }
        getEvents(rows, 0);
    };

  useEffect(() => {
    const fetchEvents = async () => {
        getEvents(rows, 0);
    };

    let appid = localStorage.getItem('appid');
    if(appid !== null){
      setAppId(appid);
      setUseAppId(true);
    }

    fetchEvents();
  }, [id]);

  const formatQueryDate = (value) => new Date(value).toLocaleString('sv').replace(' ', 'T').split('T')[0];

  const getQueryDateRange = () => {
    const [start, end] = Array.isArray(dateRange) ? dateRange : [];
    const startDate = start || new Date();
    const endDate = end || startDate;

    return {
      initDate: formatQueryDate(startDate),
      endDate: formatQueryDate(endDate),
    };
  };

  const getEvents = async (rows, page, membershipFilter = appliedMembership) => {
    try {
        setLoading(true);
        setEvents([]);
        const { initDate, endDate } = getQueryDateRange();
        setShowDebugMessage(`Fetching logs for ${type} with ID ${id} from ${initDate} to ${endDate}`);
        const client = localStorage.getItem('supportclient');
        const customerId = localStorage.getItem('id');
        const site = localStorage.getItem('supportsite');
        const appid = localStorage.getItem('appid');

        const payload = {
          clientName: client,
          locationName: site,
          customerId: Number(customerId),
          query: {
            initDate,
            endDate,
            page,
            pageSize: rows,
            membership: membershipFilter.trim() || undefined,
          },
        };

        const responseData = await supportClientLogsApi.getSupportClientLogs(payload, appid);
        setEvents(responseData);
          if(Array.isArray(responseData)){
            if(responseData.length > 0){
              for(let i = 0; i < responseData.length; i++){
                let date = new Date(responseData[i].date + 'Z');
                responseData[i].date = date.toLocaleString("en-GB");
              }
              setDataLoaded('Data loaded correctly');
            }
            else {
              setDataLoaded('No data found');
            }
            setLoading(false);

            const responseCount = await supportClientLogsApi.getSupportClientLogsCount(
              {
                ...payload,
                query: {
                  initDate,
                  endDate,
                  membership: membershipFilter.trim() || undefined,
                },
              },
              appid,
            );
            setTotalRecords(responseCount);
        }
        else {
            setDataLoaded('No information from the server');
            setLoading(false);
            setTotalRecords(0);
            setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching event record:', error);
        setDataLoaded(error.message);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            setShowLoginModal(true);
          }
          setLoading(false);
          setTotalRecords(0);
          setEvents([]);
      }
    };
  const onPageChange = (event) => {
    setLoading(true);
    setRows(event.rows);
    getEvents(event.rows, event.page, appliedMembership);
    setFirst(event.first);
  };

  const getByDate = () =>{
    const nextMembership = membership.trim();
    setAppliedMembership(nextMembership);
    setLoading(true);
    setFirst(0);
    getEvents(rows, 0, nextMembership)
  }

  const clearFilters = () => {
    setMembership('');
    setAppliedMembership('');
    setDateRange([new Date(), new Date()]);
    setFirst(0);
    setLoading(true);
    getEvents(rows, 0, '');
  };



  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  const selectDateRange = (e) => {
    setDateRange(e.value || [new Date(), new Date()]);
    setFirst(0);
    console.log('Selected date range:', e.value);
  };

  const membershipBodyTemplate = (rowData) => {
    const logText = String(rowData.log || '');
    const query = appliedMembership.trim();
    if (!query) {
      return logText;
    }

    const lowerText = logText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const start = lowerText.indexOf(lowerQuery);
    if (start < 0) {
      return logText;
    }

    const end = start + query.length;
    return (
      <>
        {logText.slice(0, start)}
        <mark>{logText.slice(start, end)}</mark>
        {logText.slice(end)}
      </>
    );
  };

  const toSafeFilePart = (value) => {
    const text = String(value || '').trim().toLowerCase();
    if (!text) {
      return '';
    }
    return text.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const downloadCsv = () => {
    if (!Array.isArray(events) || events.length === 0) {
      return;
    }

    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return '"' + text.replace(/"/g, '""') + '"';
    };

    const header = ['Log', 'Time', 'Client'];
    const lines = [header.join(',')];

    for (let i = 0; i < events.length; i++) {
      const row = events[i];
      lines.push([
        escapeCsv(row.log),
        escapeCsv(row.date),
        escapeCsv(row.client && row.client.name ? row.client.name : ''),
      ].join(','));
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const clientName = localStorage.getItem('supportclient');
    const siteName = localStorage.getItem('supportsite');
    const contextPart = toSafeFilePart(clientName) || toSafeFilePart(siteName) || 'results';
    const datePart = new Date().toISOString().slice(0, 10);

    link.setAttribute('href', url);
    link.setAttribute('download', 'support-client-logs-' + contextPart + '-' + datePart + '.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const logoutUser = async () => {
    await logout();
    navigate('/login');
  }

  return (
    <Card className='justify-content-md-center mt-5'>
    <Container fluid="md" >
    <div className="panel">
        <div className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className='flex mb-3'/> 
          <h4 className='flex mt-2 mb-3'>Logs for client {localStorage.getItem('supportclient')}</h4> 
          <span className="p-input-icon-left ml-3 mb-3">
              <i className="pi pi-search" />
              <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter Search" tooltip="Start typing to filter" 
              tooltipOptions={{position: "bottom"}}/>
          </span>
          <div className="flex align-items-center ml-3 mb-3" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <div className="card flex justify-content-center">
              <Calendar
                value={dateRange}
                onChange={(e) => selectDateRange(e)}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                showIcon
                showButtonBar
                readOnlyInput
              />
            </div>
            <span className="p-input-icon-left">
                <i className="pi pi-id-card" />
                <InputText value={membership} onChange={(e) => setMembership(e.target.value)} placeholder="Member Number" tooltip="Filter by member number" 
                tooltipOptions={{position: "bottom"}}/>
            </span>
            <div className="card flex justify-content-center">
              <Button label="Get" className="p-button-raised" onClick={getByDate} disabled={loading} />
            </div>
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button icon="pi pi-times" className="p-button-outlined" onClick={clearFilters} disabled={loading} tooltip="Clear filters" tooltipOptions={{position: "bottom"}} />
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button label="Download CSV" icon="pi pi-download" className="p-button-outlined" onClick={downloadCsv} disabled={loading || !events.length} tooltip="Download current results as CSV" tooltipOptions={{position: "bottom"}} />
          </div>
        </div>

        {appliedMembership.trim() && (
          <div className="ml-3 mb-2">
            <small>Active Member Number filter: {appliedMembership.trim()}</small>
          </div>
        )}
        
      {/* <Toolbar           
      start={
} 
        end={          
            } /> */}
    </div>
    {loading && progressbar}

    {/* <Row className='justify-content-md-center mt-5'>
      <Col sm={8}>

      </Col>
      <Col sm={4}>

      </Col>
    </Row> */}

    
    {/* <Row className='justify-content-md-center mt-5'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
        </div>
    </Row> */}
    <Toast ref={toast} />
    <Row className='justify-content-md-center mt-5'>
        <Col>
        {/* <h5 className='text-center'>Debug message: {showdebugmessage}</h5>
        <h5 className='text-center'>{dataloaded}</h5> */}
        <DataTable value={events} selectionMode={'radiobutton'} selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value.id)} 
        dataKey="id" tableStyle={{ minWidth: '50rem' }} filters={filters} filterDisplay="menu" stripedRows
        globalFilterFields={['log', 'date','client.name']} emptyMessage={loading ? "Loading client logs..." :(appliedMembership.trim() ? "No client logs found for selected date range and member number." : "No client logs found.")}
        scrollable scrollHeight="60vh">
          <Column field="log" body={membershipBodyTemplate} header="Log" showFilterMenu={false} filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }} ></Column>
            <Column field="date" header="Time"></Column>
            <Column field="client.name" header="Client"></Column>
        </DataTable>
        </Col>

    </Row>

    <Row className='justify-content-md-center mt-5'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
        </div>
    </Row>
    <div className="card flex justify-content-center">
      <Dialog 
        header="Session Expired - Please Login Again" 
        visible={showLoginModal} 
        style={{ width: '450px' }} 
        modal={true}
        closable={false}
        className="justify-content-center"
      >
        <p className="mb-3">Your session has expired. Please log in again to continue working without losing your form data.</p>
        <div className="flex justify-content-center">
          <Login onLoginSuccess={handleLoginSuccess} />
        </div>
      </Dialog>
    </div>
    </Container>
    <ScrollTop />
    </Card>
  );
};

export default SupportClientLogs;
